import React from 'react';
import { toast } from 'react-hot-toast';
import { PencilSquareIcon, BoltIcon } from '@heroicons/react/24/outline';
import api from '@/services/api';
import type { BillingProvider } from '../AdminBilling.types';

const defaultConfig = {
  publishableKey: '',
  secretKey: '',
  webhookSecret: '',
};

export const AdminBillingProviderConfigurationTab: React.FC<{
  providers: BillingProvider[];
  saving: boolean;
  setSaving: (v: boolean) => void;
  testingConnection: boolean;
  setTestingConnection: (v: boolean) => void;
  error: string | null;
  setError: (v: string | null) => void;
  loadProviders: () => Promise<void>;
  openProviderModal: (args: {
    providerKey: string;
    environment: string;
    isActive: boolean;
    publishableKey: string;
    secretKey: string;
    webhookSecret: string;
  }) => void;
}> = ({
  providers,
  saving,
  setSaving,
  testingConnection,
  setTestingConnection,
  setError,
  loadProviders,
  openProviderModal,
}) => {
  const handleTestConnection = async (p: BillingProvider) => {
    try {
      setTestingConnection(true);
      setError(null);
      await api.post('/ai/admin/billing/providers/test', {
        providerKey: p.providerKey,
        environment: p.environment,
        configJson: p.configJson ?? {},
      });
      toast.success('Connection successful.');
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'Failed to test connection';
      setError(msg);
      toast.error(msg);
    } finally {
      setTestingConnection(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Billing provider configuration</h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure API keys and webhook secrets for any payment provider (for example, Stripe). You can keep
            multiple providers configured and choose one active provider at a time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() =>
              openProviderModal({
                providerKey: 'stripe',
                environment: 'test',
                isActive: false,
                publishableKey: '',
                secretKey: '',
                webhookSecret: '',
              })
            }
            className="px-3 py-1.5 text-xs rounded-md bg-[var(--color-primary-text)] text-white hover:opacity-90"
          >
            Add provider
          </button>
        </div>
      </div>

      {providers.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Configured providers</h3>
          </div>
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Environment</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Active</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {providers.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-2 text-sm text-gray-900">{p.providerKey}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{p.environment}</td>
                    <td className="px-4 py-2 text-sm text-center">
                      <input
                        type="checkbox"
                        checked={p.isActive}
                        onChange={async (e) => {
                          const makeActive = e.target.checked;
                          try {
                            setSaving(true);
                            setError(null);

                            if (makeActive) {
                              for (const other of providers) {
                                await api.put('/ai/admin/billing/providers', {
                                  providerKey: other.providerKey,
                                  environment: other.environment,
                                  configJson: other.configJson ?? {},
                                  isActive: other.id === p.id,
                                });
                              }
                            } else {
                              await api.put('/ai/admin/billing/providers', {
                                providerKey: p.providerKey,
                                environment: p.environment,
                                configJson: p.configJson ?? {},
                                isActive: false,
                              });
                            }

                            await loadProviders();
                            toast.success(makeActive ? 'Provider set active.' : 'Provider set inactive.');
                          } catch (err: any) {
                            const msg =
                              err?.response?.data?.message ||
                              err?.response?.data?.error ||
                              'Failed to update active provider';
                            setError(msg);
                            toast.error(msg);
                          } finally {
                            setSaving(false);
                          }
                        }}
                        className="rounded border-gray-300 text-[var(--color-primary-text)] focus:ring-[var(--color-primary-text)]"
                      />
                    </td>
                    <td className="px-4 py-2 text-sm text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            const cfg = { ...defaultConfig, ...(p.configJson || {}) } as typeof defaultConfig;
                            openProviderModal({
                              providerKey: p.providerKey,
                              environment: p.environment,
                              isActive: p.isActive,
                              publishableKey: cfg.publishableKey || '',
                              secretKey: cfg.secretKey ? '••••••••' : '',
                              webhookSecret: cfg.webhookSecret ? '••••••••' : '',
                            });
                          }}
                          className="p-1.5 rounded text-gray-500 hover:text-[var(--color-primary-text)] hover:bg-[var(--color-hover-bg)]"
                          title="Edit provider"
                          aria-label="Edit provider"
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                        {p.providerKey.toLowerCase() === 'stripe' && (
                          <button
                            type="button"
                            onClick={() => void handleTestConnection(p)}
                            disabled={testingConnection}
                            className="p-1.5 rounded text-gray-500 hover:text-[var(--color-primary-text)] hover:bg-[var(--color-hover-bg)] disabled:opacity-50"
                            title="Test connection"
                            aria-label="Test connection"
                          >
                            <BoltIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {saving && <p className="text-xs text-gray-500 mt-2">Saving…</p>}
        </div>
      )}

      {providers.length === 0 && (
        <p className="text-sm text-gray-600">No providers configured yet. Add one to connect Stripe (or another provider).</p>
      )}
    </div>
  );
};

